<template>
  <div class='day' :style='rootStyle'>
    <!-- watercolor and mask canvases -->
    <canvas ref='canvas1' :width='2 * width' :height='2 * imageHeight'
      class='canvas' :style='canvasStyle' />
    <canvas ref='canvas2' :width='2 * width' :height='2 * imageHeight'
      class='canvas' :style='canvasStyle' />
    <svg ref='svg' :style='canvasStyle' class='canvas'>
      <g :transform='`scale(${scale})`'>
        <!-- flower strokes -->
        <g class='shapes' />
        <!-- hover -->
        <g class='hover'>
          <defs>
            <!-- arrowhead marker definition -->
            <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5"
              markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill='#ff483b' />
            </marker>
          </defs>
          <circle v-for='circle in hoverShapes.circles' v-bind='circle' />
          <path v-for='line in hoverShapes.lines' v-bind='line' fill='none' />
        </g>
      </g>
    </svg>
    <svg class='canvas' width='100%' height='3'>
      <line class='progress' :x2='`${progress * 100}%`' />
    </svg>
    <!-- info -->
    <Infobox v-bind='{
      width, height: height - scale * imageHeight,
      top: scale * imageHeight, formattedDate: texts[0].formattedDate,
      currentTime, data: featuredTexts, selected: selectedText, index, featuredIndex,
    }' />
    <!-- translucent cover -->
    <div class='cover' @click='navigateIndex(index)'
      :style='{display: featuredIndex === index ? "none" : "block"}' />
    <!-- arrows for navigating -->
    <div v-if='featuredIndex === index' class='arrows'>
      <div v-if='index > 0' :style='{left: isMobile ? `2px` : `6px`}'
        @click='navigateIndex(index - 1)'>←</div>
      <div v-if='index < 6' :style='{right: isMobile ? `2px` : `6px`}'
        @click='navigateIndex(index + 1)'>→</div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import _ from 'lodash';
import p5 from 'p5';
import {TimelineLite} from 'gsap';
import Flower from './Flower';
import Infobox from './Infobox.vue';
import Utils from './utils';

const progressHeight = 5;
const tlDuration = 30;
const yellow = [250,214,125];
const red = '#ff483b';

export default {
  props: [
    'headerHeight', 'footerHeight', 'isMobile', 'texts',
    'flowerSizeScale', 'leafSizeScale', 'numPetalScale',
    'journey', 'index', 'featuredIndex', 'replay',
  ],
  name: 'Day',
  components: {Infobox},
  data() {
    const scale = this.index === this.featuredIndex ? 1 : 0.75;
    const margin = this.isMobile ? 10 : 30;
    const width = this.isMobile ? window.innerWidth - 2 * margin : 500;
    const imageHeight = this.isMobile ? 350 : 400;
    const height = 700;
    return {
      width, height, imageHeight, scale, margin,
      canvasStyle: {width: `${scale * width}px`, height: `${scale * imageHeight}px`},
      rootStyle: this.calculateStyle(width),
      featuredTexts: [],
      selectedText: null,
      timeScale: d3.scaleTime(),
      progress: null,
      currentTime: null,
      hoverShapes: {lines: [], circles: []},
    }
  },
  watch: {
    texts: function() {
      this.featuredTexts = [];
      this.selectedText = null;
      this.clearCanvas();
      this.tl.clear(true);

      this.setupScales();
      this.calculatePositions();
      this.prepareAnimation();

      this.tl.restart();
    },
    replay: function() {
      this.featuredTexts = [];
      this.selectedText = null;
      this.clearCanvas();
      this.tl.restart();
    },
    featuredIndex: function() {
      this.rootStyle = this.calculateStyle(this.width);
      this.scale = this.index === this.featuredIndex ? 1 : 0.75;
      this.canvasStyle = {
        width: `${this.scale * this.width}px`,
        height: `${this.scale * this.imageHeight}px`
      };
    },
  },
  methods: {
    calculateStyle: function(width) {
      let gridColumn;
      let gridRow;
      if (!this.isMobile && this.index === this.featuredIndex) {
        gridColumn = `1 / 2`;
        gridRow = '1 / 3';
      }
      return {
        position: 'relative',
        gridColumn, gridRow,
        width: '100%',
        overflow: 'hidden',
      };
    },
    clearCanvas: function() {
      this.ctx1.clearRect(0, 0, this.width, this.imageHeight);
      this.ctx2.clearRect(0, 0, this.width, this.imageHeight);

      this.ctx2.globalCompositeOperation = 'source-over';
      this.ctx2.fillStyle = '#fff';
      this.ctx2.fillRect(0, 0, this.width, this.imageHeight);
      this.ctx2.globalCompositeOperation = 'destination-out';
      // progress bar
      this.ctx2.fillRect(0, this.imageHeight - progressHeight, this.width, progressHeight);

      this.container.select('.shapes').selectAll('*').remove();
    },
    setupScales: function() {
      const minDate = d3.timeDay.floor(this.texts[0].date);
      const maxDate = d3.timeMinute.offset(minDate, 1439);
      this.timeScale.domain([minDate, maxDate]);
    },
    calculatePositions: function() {
      let index = -1;
      _.chain(this.texts)
        .groupBy(text => d3.timeDay.floor(text.date))
        .each((texts, day) => {
          index += 1;
          // if (index !== 5) return;
          _.each(texts, d => {
            const progress = this.timeScale(d.date); // divided by ms in a day
            const size = (d.sentiment > 0 ? this.flowerSizeScale : this.leafSizeScale)(d.keyword.length);
            const numPetals = d.sentiment > 0 ? this.numPetalScale(d.entropy) : 1;
            Object.assign(d, {progress, size, numPetals});
          });

          this.fractalPositions(null, texts, -Math.PI / 2, 0, 1);
        }).value();

      // register voronoi
      this.voronoi = d3.voronoi().x(d => d.x).y(d => d.y)(this.texts);
    },
    fractalPositions: function(prev, shapes, initialAngle, direction, level) {
      const shape = shapes.shift(); // get the first shape
      if (!shape) return;
      const origin = prev || {x: this.width / 2, y: this.imageHeight};

      let radius = prev ? prev.size / (prev.sentiment > 0 ? 1 : 2) : 0;
      radius += shape.size / (shape.sentiment > 0 ? 1 : 2);
      radius += 1.5 * _.filter(shapes, d => d.sentiment > 0).length;
      radius += 0.75 * _.filter(shapes, d => d.sentiment === 0).length;
      const px = radius * Math.cos(initialAngle) + origin.x;
      const py = radius * Math.sin(initialAngle) + origin.y; // first extend it from previous point

      let angle = direction * p5.prototype.randomGaussian(Math.PI / 6, 0.1);
      const {x, y} = Utils.RotateVector({x: px, y: py}, angle, origin);
      angle = Math.atan2(y - origin.y, x - origin.x);
      Object.assign(shape, {angle, radius, x, y, prev: origin, level});

      const half = Math.floor(shapes.length / 2);
      this.fractalPositions(
        shape, shapes.slice(0, half),
        angle, -1, level + 0.5,
      );
      this.fractalPositions(
        shape, shapes.slice(half, shapes.length),
        angle, 1, level + 0.5,
      );
    },
    prepareAnimation: function() {
      const perProgressWidth = this.width / this.texts.length;
      _.each(this.texts, (text, i) => {
        this.tl.add(() => {
          // get watercolor ready
          this.watercolorWorker.postMessage([{
            // branch
            color: text.satColors[2],
            branch: true, x1: text.prev.x, x2: text.x, y1: text.prev.y, y2: text.y,
          }, {
            // 2 colors
            color: text.satColors[0],
            r: 0.9 * text.size, x: text.x + text.size / 8, y: text.y + text.size / 8,
          }, {
            color: text.satColors[1],
            r: 0.9 * text.size, x: text.x - text.size / 8, y: text.y - text.size / 8,
          }]);
          // then set up flower
          Flower.drawFlower(this.svg, this.ctx2, this.tl, Object.assign({}, text, {
            cx: text.x, cy: text.y,
            color: text.satColors[0],
            initialAngle: text.angle / Math.PI * 180,
          }));

          // and add information
          this.featuredTexts.unshift(text);
          // this.selectedText = text;
        }, tlDuration * text.progress);
      });
    },
    drawWatercolor: function(layers) {
      _.each(layers, layer => {
        this.ctx1.fillStyle = `rgba(${layer.color}, 0.25)`;
        this.ctx1.beginPath();
        _.each(layer.points, (point, i) => {
          const [x, y] = point;
          if (i === 0) {
            this.ctx1.moveTo(x, y);
            return;
          }
          this.ctx1.lineTo(x, y);
        });

        this.ctx1.fill();
      });
    },
    hoverArt: function() {
      const [x, y] = d3.mouse(this.$refs.svg);
      const text = this.voronoi.find(this.scale * x, this.scale * y, 30);
      if (text && text.data === this.selectedText) return; // if it's same, don't do anything

      if (text && _.includes(this.featuredTexts, text.data)) {
        this.selectedText = text.data;
        const {nextText, prevText} = this.selectedText;
        const circles = [
          // current circle
          {cx: this.selectedText.x, cy: this.selectedText.y, r: 3.5, fill: red},
          {cx: this.selectedText.x, cy: this.selectedText.y, r: 8, fill: 'none', stroke: red},
        ];
        const lines = [];

        // if next text is already shown, put a line with arrow there
        if (_.includes(this.featuredTexts, nextText)) {
          // circles.push({cx: nextText.x, cy: nextText.y, r: 2.5, fill: red});
          lines.push({
            d: this.calculateCurve(this.selectedText, nextText),
            stroke: red, 'marker-start': 'url(#arrow)',
          });
        }
        // if there's prev text, put a line
        if (prevText) {
          circles.push({cx: prevText.x, cy: prevText.y, r: 2, fill: red});
          lines.push({
            d: this.calculateCurve(prevText, this.selectedText), stroke: red,
          });
        }

        this.hoverShapes = {circles, lines};
      } else {
        this.selectedText = null;
        this.hoverShapes = {circles: [], lines: []};
      }
    },
    unhoverArt: function() {
      this.selectedText = null;
      this.hoverShapes = {circles: [], lines: []};
    },
    clickArt: function() {
      if (!this.selectedText) return;
      window.open(this.selectedText.artwork.url, '_new');
    },
    navigateIndex: function(index) {
      this.$router.push({name: "Art", params: {
        journey: this.journey,
        index,
      }});
    },
    calculateCurve: function(prev, next) {
      const x1 = prev.x;
      const y1 = prev.y;
      const x2 = next.x;
      const y2 = next.y;
      let mx = (x1 + x2) / 2;
      let my = (y1 + y2) / 2;
      const {x, y} = Utils.RotateVector({x: mx, y: my}, -Math.PI / 4, {x: x1, y: y1});
      return `M${x2},${y2} Q${x},${y} ${x1},${y1}`;
    },
  },
  created() {
    this.watercolorWorker = new Worker(`./static/Watercolor-worker.js`);
    this.tl = new TimelineLite({
      paused: true,
      onUpdate: () => {
        this.progress = this.tl.progress();
        this.currentTime = this.timeScale.invert(this.progress);
      }
    });
  },
  mounted() {
    this.container = d3.select(this.$el);
    this.svg = d3.select(this.$refs.svg);
    this.svg.on('click', this.clickArt)
      .on('mousemove', this.hoverArt).on('mouseleave', this.unhoverArt);
    this.ctx1 = this.$refs.canvas1.getContext('2d');
    this.ctx2 = this.$refs.canvas2.getContext('2d');
    // crispy canvas
    this.ctx1.scale(2, 2);
    this.ctx2.scale(2, 2);

    this.watercolorWorker.onmessage = (event) => {
      this.drawWatercolor(event.data.layers);
    }

    this.clearCanvas();
    this.setupScales();
    this.calculatePositions();
    this.prepareAnimation();
    this.tl.restart();
  },
}
</script>

<style scoped>
.day {
  display: inline-block;
  padding: 0;
  vertical-align: top;
  /* box-shadow: 0 2px 8px 2px rgba(68,73,76,0.2); */
  border: 1px solid rgba(68,73,76,0.2);
  background-color: #fff;
}

.canvas {
  position: absolute;
  top: 0;
  left: 50%;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%)
}

.cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0);
  cursor: pointer;
}

svg line.progress {
  stroke: #ff483b;
  stroke-width: 4;
}

.hover path {
  stroke-dasharray: 5;
  animation: dash 60s linear infinite;
  stroke-width: 1.5;
}

@keyframes dash {
  to {
    stroke-dashoffset: 500;
  }
}

.arrows {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
}

.arrows div {
  position: absolute;
  font-size: 18px;
  height: 24px;
  width: 24px;
  line-height: 24px;
  border-radius: 24px;
  text-align: center;
  background-color: #ff483b;
  color: #fff;
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/Day.vue